import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
  createStylesParam,
  IStyleParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/tpa-style-webpack-plugin/dist/runtime/types';
import {
  ProviderColorTheme,
  SSOButtonType,
} from '../common/SocialButtonsOptions/constants';
import {
  getThemeStyles,
  ThemeConfig,
} from '../common/SocialButtonsSection/providerThemeStyles';
import { IDP_APPDEFIDS } from '../../constants';
import { DISPLAY_TOGGLE_DEFAULTS } from '../../constants/displayToggleDefaults';
import { buttonStyleColor } from '../common/Fields/buttonStyleConfig';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import { DividerTypeOptions } from '../common/DividerSection/DividerTypeOptions';
import resolvedLayoutParams from './layoutParams';
import {
  AlignmentOptions,
  resolveTextAlign,
  resolveJustifyContent,
  resolveFlexAlignment,
} from '../common/AlignmentThumbnails/AlignmentOptions';
import { getShadowOffsets } from '../utils/shadow-offsets';
import {
  SocialButtonStyle,
  SocialButtonsPlacement,
} from '../../constants/layoutConstants';
import { BorderStyleOptions } from '../common/Fields/BorderStyleOptions';
import { getProviderColorDefault } from '../utils/colorUtils';

// Numeric defaults used as fallbacks in customCssVars when a style param
// has not been set by the user. Keep in sync with the param definitions below.
const DEFAULTS = {
  shadowAngle: 135,
  shadowDistance: 0,
  closeButtonIconSize: 24,
  spacingBetweenFormElements: 16,
  inputFieldBorderStyle: 1,
  inputFieldBorderWidth: 1,
  socialButtonsSpacing: 16,
  socialIconSize: 32,
  socialFontIconSize: 16,
  topAndBottomPadding: 60,
  logoSize: 120,
} as const;

// Convenience functions for common cases
const color = (value: any) => ({
  type: StyleParamType.Color as const,
  getDefaultValue: () => value,
});

const wixColor = (colorKey: string, opacity?: number) => ({
  type: StyleParamType.Color as const,
  getDefaultValue: wixColorParam(colorKey, opacity),
});

const font = (fontKey: string, styleOverrides?: any) => ({
  type: StyleParamType.Font as const,
  getDefaultValue: wixFontParam(fontKey as any, styleOverrides),
});

const number = (value: number) => ({
  type: StyleParamType.Number as const,
  getDefaultValue: () => value,
});

const boolean = (value: boolean) => ({
  type: StyleParamType.Boolean as const,
  getDefaultValue: () => value,
});

// Create the signupButtonStyle parameter first so it can be referenced
const signupButtonStyle = createStylesParam('signupButtonStyle', {
  type: StyleParamType.Number,
  getDefaultValue: () => 1, // Default to round rectangle filled
  metadata: {
    type: StyleType.customEnum,
    defaultValue: { type: 'static', value: 1 },
    ai: {
      description: 'Visual preset for the submit button shape and fill style',
    },
    options: {
      type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
      items: [
        { value: 0, displayName: 'Rectangle Filled' },
        { value: 1, displayName: 'Round Rectangle Filled' },
        { value: 2, displayName: 'Pill Filled' },
        { value: 3, displayName: 'Rectangle Outline' },
        { value: 4, displayName: 'Round Rectangle Outline' },
        { value: 5, displayName: 'Pill Outline' },
      ],
    },
  },
});

const googleColorTheme = createStylesParam('googleColorTheme', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0, // Default to Outline theme
  metadata: {
    type: StyleType.customEnum,
    defaultValue: { type: 'static', value: 0 },
    ai: { description: 'Color theme for the Google signup button' },
    options: {
      type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
      items: [
        { value: 0, displayName: 'Outline' },
        { value: 1, displayName: 'Light' },
        { value: 2, displayName: 'Dark' },
      ],
    },
  },
});

const facebookColorTheme = createStylesParam('facebookColorTheme', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0, // Default to Outline theme
  metadata: {
    type: StyleType.customEnum,
    defaultValue: { type: 'static', value: 0 },
    ai: { description: 'Color theme for the Facebook signup button' },
    options: {
      type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
      items: [
        { value: 0, displayName: 'Outline' },
        { value: 1, displayName: 'Light' },
        { value: 2, displayName: 'Dark' },
      ],
    },
  },
});

const ssoColorTheme = createStylesParam('ssoColorTheme', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0, // Default to Outline theme
  metadata: {
    type: StyleType.customEnum,
    defaultValue: { type: 'static', value: 0 },
    ai: { description: 'Color theme for the SSO signup button' },
    options: {
      type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
      items: [
        { value: 0, displayName: 'Outline' },
        { value: 1, displayName: 'Light' },
        { value: 2, displayName: 'Dark' },
      ],
    },
  },
});

// Display toggle parameters (elements panel)
const showTitle = createStylesParam('showTitle', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => DISPLAY_TOGGLE_DEFAULTS.showTitle,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: { type: 'static', value: DISPLAY_TOGGLE_DEFAULTS.showTitle },
    ai: { description: 'Toggle visibility of the form title' },
  },
});

const showSubtitle = createStylesParam('showSubtitle', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => DISPLAY_TOGGLE_DEFAULTS.showSubtitle,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: DISPLAY_TOGGLE_DEFAULTS.showSubtitle,
    },
    ai: { description: 'Toggle visibility of the form subtitle' },
  },
});

const showFormSwitchBar = createStylesParam('showFormSwitchBar', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => DISPLAY_TOGGLE_DEFAULTS.showFormSwitchBar,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: DISPLAY_TOGGLE_DEFAULTS.showFormSwitchBar,
    },
    ai: { description: 'Toggle visibility of the form switch bar' },
  },
});

const showMandatorySSOSubtitle = createStylesParam('showMandatorySSOSubtitle', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: { type: 'static', value: true },
    ai: { description: 'Toggle visibility of the mandatory SSO subtitle' },
  },
});

// Helper function to create theme-based style getters for colors
const providerColor = (
  themeParam: any,
  providerId: string,
  styleProperty: keyof ThemeConfig['styles'],
) => ({
  type: StyleParamType.Color as const,
  getDefaultValue: ({ getStyleParamValue }: any) => {
    const theme = getStyleParamValue(themeParam) as ProviderColorTheme;
    return getThemeStyles(theme, providerId, styleProperty);
  },
});

// Enum items extracted to module-level constants. Inline arrays inside
// `as const` STYLE_PARAM_DEFINITIONS would become readonly tuples, but the
// library's metadata.options.items type expects mutable `any[]`.
const ALIGNMENT_ENUM_ITEMS = [
  { value: AlignmentOptions.LEFT, displayName: 'Left' },
  { value: AlignmentOptions.CENTER, displayName: 'Center' },
  { value: AlignmentOptions.RIGHT, displayName: 'Right' },
];

const TITLE_BORDER_STYLE_ENUM_ITEMS = [
  { value: 0, displayName: 'None' },
  { value: 1, displayName: 'Box' },
  { value: 2, displayName: 'Line' },
];

const DIVIDER_TYPE_ENUM_ITEMS = [
  { value: 0, displayName: 'Line and Text' },
  { value: 1, displayName: 'Line Only' },
  { value: 2, displayName: 'Text Only' },
];

const FORM_SWITCH_BAR_STYLE_ENUM_ITEMS = [
  { value: 0, displayName: 'Button' },
  { value: 1, displayName: 'Link' },
];

const SUBMIT_BUTTON_STATE_ENUM_ITEMS = [
  { value: 0, displayName: 'Regular' },
  { value: 1, displayName: 'Hover' },
];

// Parameter definitions dictionary

const STYLE_PARAM_DEFINITIONS = {
  // Background
  formBackground: {
    ...wixColor('color-fill-background-primary', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: { description: 'Background color of the signup form container' },
    },
  },
  borderColor: {
    ...wixColor('color-line', 0),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 0 },
      ai: { description: 'Border color of the signup form container' },
    },
  },
  borderWidth: {
    ...number(0),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Border width of the signup form container in pixels',
      },
    },
  },
  textFont: {
    ...font('Body-M', { size: 20 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 20 },
      ai: { description: 'Font for general text content' },
    },
  },
  textColor: {
    ...wixColor('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Color of general text content' },
    },
  },
  // Title and subtitle parameters
  titleFont: {
    ...font('Page-title', { size: 32 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 32 },
      ai: { description: 'Font style for the form title' },
    },
  },
  titleColor: {
    ...wixColor('color-title', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-title', opacity: 1 },
      ai: { description: 'Text color of the form title' },
    },
  },
  subtitleFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the form subtitle' },
    },
  },
  subtitleColor: {
    ...wixColor('color-text-secondary', 0.8),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-secondary',
        opacity: 0.8,
      },
      ai: { description: 'Text color of the form subtitle' },
    },
  },

  // Profile Privacy parameters
  profilePrivacyFootnoteColor: {
    ...wixColor('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: { description: 'Color of the profile privacy footnote text' },
    },
  },
  profilePrivacyFootnoteFont: {
    ...font('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: { description: 'Font for the profile privacy footnote text' },
    },
  },
  profilePrivacyFootnoteTextAlignment: {
    type: StyleParamType.Number as const,
    getDefaultValue: ({ isRTL }: { isRTL: boolean }) =>
      isRTL ? AlignmentOptions.RIGHT : AlignmentOptions.LEFT,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.LEFT },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: ALIGNMENT_ENUM_ITEMS,
      },
      ai: {
        description: 'Horizontal alignment of the profile privacy footnote',
      },
    },
  },

  // Form switch bar
  formSwitchBarPrefixFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the form switch bar prefix text' },
    },
  },
  formSwitchBarPrefixColor: {
    ...wixColor('color-text-secondary', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-secondary',
        opacity: 1,
      },
      ai: { description: 'Color of the form switch bar prefix text' },
    },
  },
  formSwitchBarLinkFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the form switch bar link' },
    },
  },
  formSwitchBarLinkColor: {
    ...wixColor('color-action', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-action',
        opacity: 1,
      },
      ai: { description: 'Color of the form switch bar link' },
    },
  },
  // Layout
  titleTextAlignment: {
    type: StyleParamType.Number as const,
    getDefaultValue: () => AlignmentOptions.CENTER,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.CENTER },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: ALIGNMENT_ENUM_ITEMS,
      },
      ai: { description: 'Horizontal alignment of the form title' },
    },
  },
  formSwitchBarTextAlignment: {
    type: StyleParamType.Number as const,
    getDefaultValue: () => AlignmentOptions.CENTER,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.CENTER },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: ALIGNMENT_ENUM_ITEMS,
      },
      ai: { description: 'Horizontal alignment of the form switch bar' },
    },
  },

  errorColor: {
    ...color({
      value: '#DF3131',
      name: null,
      opacity: 1,
    }),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'custom', opacity: 1 },
      ai: { description: 'Color of error messages and validation feedback' },
    },
  },
  errorCornerRadius: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of error state elements in pixels' },
    },
  },

  // Input field parameters
  inputFieldBorderStyle: {
    ...number(1),
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Border style of form input fields' },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: TITLE_BORDER_STYLE_ENUM_ITEMS,
      },
    },
  },
  inputFieldBorderColor: {
    ...wixColor('color-line', 0.6),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 0.6 },
      ai: { description: 'Border color of form input fields' },
    },
  },
  inputFieldBorderWidth: {
    ...number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Border width of form input fields in pixels' },
    },
  },
  inputFieldFillColor: {
    ...wixColor('color-fill-background-primary', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: { description: 'Background fill color of form input fields' },
    },
  },
  inputFieldCornerRadius: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of form input fields in pixels' },
    },
  },
  inputFieldTitleFont: {
    ...font('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: { description: 'Font for input field labels' },
    },
  },
  inputFieldTitleColor: {
    ...wixColor('color-text-primary', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Color of input field labels' },
    },
  },
  inputFieldInputFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for input field text' },
    },
  },
  inputFieldInputColor: {
    ...wixColor('color-text-secondary', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-secondary',
        opacity: 1,
      },
      ai: { description: 'Color of input field text' },
    },
  },
  inputFieldPlaceholderColor: {
    ...wixColor('color-text-secondary'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-secondary',
        opacity: 1,
      },
      ai: { description: 'Color of input field placeholder text' },
    },
  },
  inputFieldLinkTextColor: {
    ...wixColor('color-action', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-action', opacity: 1 },
      ai: { description: 'Color of links within form input fields' },
    },
  },
  inputFieldcheckmarkColor: {
    ...wixColor('color-action', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-action', opacity: 1 },
      ai: { description: 'Color of checkboxes and selection indicators' },
    },
  },
  inputFieldDescriptionFont: {
    ...font('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: { description: 'Font for input field descriptions and helper text' },
    },
  },
  inputFieldDescriptionColor: {
    ...wixColor('color-text-secondary'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-secondary',
        opacity: 1,
      },
      ai: { description: 'Color of input field descriptions and helper text' },
    },
  },

  // Input field hover state
  inputFieldHoverFillColor: {
    ...wixColor('color-fill-background-primary', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: { description: 'Background fill color of input fields on hover' },
    },
  },
  inputFieldHoverBorderColor: {
    ...wixColor('color-line', 1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of input fields on hover' },
    },
  },

  // Close button parameters
  closeButtonIconColor: {
    ...wixColor('color-text-primary', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Icon color of the close button' },
    },
  },
  closeButtonIconSize: {
    ...number(24),
    metadata: {
      type: StyleType.fontSize,
      defaultValue: { type: 'static', value: 24 },
      ai: { description: 'Icon size of the close button in pixels' },
    },
  },
  closeButtonBackgroundColor: {
    ...wixColor('color-fill-background-primary', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 0,
      },
      ai: { description: 'Background color of the close button' },
    },
  },

  // Shadow parameters
  enableShadow: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: { description: 'Toggle the box shadow on the form container' },
    },
  },
  shadowAngle: {
    ...number(135),
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 135 },
      ai: { description: 'Angle of the box shadow in degrees' },
    },
  },
  shadowDistance: {
    ...number(0),
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Distance of the box shadow in pixels' },
    },
  },
  shadowSize: {
    ...number(0),
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Size spread of the box shadow in pixels' },
    },
  },
  shadowBlur: {
    ...number(25),
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 25 },
      ai: { description: 'Blur radius of the box shadow in pixels' },
    },
  },
  shadowColor: {
    ...wixColor('color-text-primary', 0.15),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 0.15,
      },
      ai: { description: 'Color of the box shadow' },
    },
  },
  boxShadow: {
    type: StyleParamType.Number as const,
    getDefaultValue: () => undefined as any,
    metadata: {
      type: StyleType.boxShadow,
      defaultValue: {
        type: 'static',
        value: {
          offsetX: 0,
          offsetY: 0,
          blurRadius: 25,
          spreadRadius: 0,
          color: { value: '#000000', opacity: 0.15 },
        },
      },
      ai: {
        description:
          'Composite box shadow definition (offsetX, offsetY, blurRadius, spreadRadius, color). When set, overrides the individual shadow parameters.',
      },
    },
  },

  // Border radius parameters
  borderRadiusTopLeft: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Top-left corner radius of the form container in pixels',
      },
    },
  },
  borderRadiusTopRight: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Top-right corner radius of the form container in pixels',
      },
    },
  },
  borderRadiusBottomLeft: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius of the form container in pixels',
      },
    },
  },
  borderRadiusBottomRight: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius of the form container in pixels',
      },
    },
  },

  // Divider styling parameters
  dividerType: {
    ...number(DividerTypeOptions.LINE_AND_TEXT),
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Visual style of the divider between sections' },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: DIVIDER_TYPE_ENUM_ITEMS,
      },
    },
  },
  dividerTextFont: {
    ...font('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: { description: 'Font for the divider text' },
    },
  },
  dividerTextColor: {
    ...wixColor('color-text-secondary', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-secondary',
        opacity: 1,
      },
      ai: { description: 'Color of the divider text' },
    },
  },
  dividerTextAlignment: {
    type: StyleParamType.Number as const,
    getDefaultValue: () => AlignmentOptions.CENTER,
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: AlignmentOptions.CENTER },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: ALIGNMENT_ENUM_ITEMS,
      },
      ai: { description: 'Horizontal alignment of the divider text' },
    },
  },
  dividerLineColor: {
    ...wixColor('color-line', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 0.3 },
      ai: { description: 'Color of the divider line' },
    },
  },
  dividerLineWidth: {
    ...number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Width of the divider line in pixels' },
    },
  },

  // Social Signup Options parameters
  socialButtonCornerRadius: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of social signup buttons in pixels' },
    },
  },
  socialFontIconSize: {
    ...number(16),
    metadata: {
      type: StyleType.fontSize,
      defaultValue: { type: 'static', value: 16 },
      ai: { description: 'Font icon size in social buttons in pixels' },
    },
  },
  socialIconSize: {
    ...number(32),
    metadata: {
      type: StyleType.fontSize,
      defaultValue: { type: 'static', value: 32 },
      ai: { description: 'Icon size in social buttons in pixels' },
    },
  },

  // Social button color themes
  googleColorTheme,
  facebookColorTheme,
  ssoColorTheme,

  // Google provider parameters
  googleBorderColor: {
    ...providerColor(googleColorTheme, IDP_APPDEFIDS.google, 'borderColor'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of the Google signup button' },
    },
  },
  googleTextFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the Google signup button text' },
    },
  },
  googleTextColor: {
    ...providerColor(googleColorTheme, IDP_APPDEFIDS.google, 'textColor'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the Google signup button' },
    },
  },
  googleFillColor: {
    ...providerColor(googleColorTheme, IDP_APPDEFIDS.google, 'backgroundColor'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'custom', opacity: 1 },
      ai: { description: 'Background fill color of the Google signup button' },
    },
  },
  googleHoverFillColor: {
    ...providerColor(
      googleColorTheme,
      IDP_APPDEFIDS.google,
      'hoverBackgroundColor',
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'custom', opacity: 1 },
      ai: {
        description:
          'Background fill color of the Google signup button on hover',
      },
    },
  },

  // Facebook provider parameters
  facebookBorderColor: {
    ...providerColor(facebookColorTheme, IDP_APPDEFIDS.facebook, 'borderColor'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of the Facebook signup button' },
    },
  },
  facebookTextFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the Facebook signup button text' },
    },
  },
  facebookTextColor: {
    ...providerColor(facebookColorTheme, IDP_APPDEFIDS.facebook, 'textColor'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the Facebook signup button' },
    },
  },
  facebookFillColor: {
    ...providerColor(
      facebookColorTheme,
      IDP_APPDEFIDS.facebook,
      'backgroundColor',
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'custom', opacity: 1 },
      ai: {
        description: 'Background fill color of the Facebook signup button',
      },
    },
  },
  facebookHoverFillColor: {
    ...providerColor(
      facebookColorTheme,
      IDP_APPDEFIDS.facebook,
      'hoverBackgroundColor',
    ),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'custom', opacity: 1 },
      ai: {
        description:
          'Background fill color of the Facebook signup button on hover',
      },
    },
  },

  // SSO provider parameters
  ssoButtonType: {
    ...number(0),
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Display style of the SSO button: Button or Link' },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: FORM_SWITCH_BAR_STYLE_ENUM_ITEMS,
      },
    },
  },
  ssoShowIcon: {
    ...boolean(true),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Toggle visibility of the SSO button icon' },
    },
  },

  // SSO Button mode styling
  ssoButtonTextFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the SSO button text' },
    },
  },
  ssoButtonTextColor: {
    ...providerColor(ssoColorTheme, IDP_APPDEFIDS.sso, 'textColor'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the SSO button' },
    },
  },
  ssoButtonFillColor: {
    ...providerColor(ssoColorTheme, IDP_APPDEFIDS.sso, 'backgroundColor'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: { description: 'Background fill color of the SSO button' },
    },
  },
  ssoButtonBorderColor: {
    ...providerColor(ssoColorTheme, IDP_APPDEFIDS.sso, 'borderColor'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of the SSO button' },
    },
  },

  // SSO Link mode styling
  ssoLinkTextFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the SSO link text' },
    },
  },
  ssoLinkHoverTextFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the SSO link text on hover' },
    },
  },
  ssoLinkHoverTextFontCustomized: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'When true, the hover font is customized independently; when false, it mirrors the regular state font',
      },
    },
  },
  ssoLinkTextColor: {
    ...wixColor('color-action', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-action', opacity: 1 },
      ai: { description: 'Text color of the SSO link' },
    },
  },
  ssoLinkHoverTextColor: {
    ...wixColor('color-action', 0.7),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-action', opacity: 0.7 },
      ai: { description: 'Text color of the SSO link on hover' },
    },
  },

  // SSO hover state parameters
  ssoHoverFontBold: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: { description: 'Apply bold to SSO hover text' },
    },
  },
  ssoHoverFontItalic: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: { description: 'Apply italic to SSO hover text' },
    },
  },
  ssoHoverFontUnderline: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: { description: 'Apply underline to SSO hover text' },
    },
  },
  ssoHoverTextColor: {
    ...wixColor('color-action', 0.7),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-action', opacity: 0.7 },
      ai: { description: 'Text color of SSO elements on hover' },
    },
  },
  ssoHoverFillColor: {
    ...wixColor('color-fill-background-primary', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: { description: 'Background fill color of SSO elements on hover' },
    },
  },
  ssoHoverBorderColor: {
    ...wixColor('color-line', 1),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of SSO elements on hover' },
    },
  },

  // SSO Button hover state parameters (for Button mode only)
  ssoButtonHoverTextFont: {
    ...font('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the SSO button text on hover' },
    },
  },
  ssoButtonHoverTextFontCustomized: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'When true, the hover font is customized independently; when false, it mirrors the regular state font',
      },
    },
  },
  ssoButtonHoverTextColor: {
    ...providerColor(ssoColorTheme, IDP_APPDEFIDS.sso, 'hoverTextColor'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the SSO button on hover' },
    },
  },
  ssoButtonHoverFillColor: {
    ...providerColor(ssoColorTheme, IDP_APPDEFIDS.sso, 'hoverBackgroundColor'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-fill-background-primary',
        opacity: 1,
      },
      ai: { description: 'Background fill color of the SSO button on hover' },
    },
  },
  ssoButtonHoverBorderColor: {
    ...providerColor(ssoColorTheme, IDP_APPDEFIDS.sso, 'hoverBorderColor'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of the SSO button on hover' },
    },
  },

  // Signup button parameters
  signupButtonState: {
    ...number(0),
    metadata: {
      type: StyleType.customEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Current design state being edited: Regular or Hover',
      },
      options: {
        type: CSS_PROPERTIES.CSS_DATA_TYPE.number,
        items: SUBMIT_BUTTON_STATE_ENUM_ITEMS,
      },
    },
  },
  signupButtonStyle,
  signupButtonBorderWidth: {
    ...number(1),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Border width of the submit button in pixels' },
    },
  },
  signupButtonCornerRadius: {
    ...number(0),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of the submit button in pixels' },
    },
  },
  // Signup button regular state
  signupButtonRegularFont: {
    ...font('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the submit button text' },
    },
  },
  signupButtonRegularColor: {
    ...buttonStyleColor(signupButtonStyle, 'textColor'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'button-color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the submit button' },
    },
  },
  signupButtonRegularFillColor: {
    ...buttonStyleColor(signupButtonStyle, 'fillColor'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'button-color-fill-primary',
        opacity: 1,
      },
      ai: { description: 'Background fill color of the submit button' },
    },
  },
  signupButtonRegularBorderColor: {
    ...buttonStyleColor(signupButtonStyle, 'borderColor'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of the submit button' },
    },
  },
  // Signup button hover state
  signupButtonHoverFont: {
    ...font('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font for the submit button text on hover' },
    },
  },
  signupButtonHoverFontCustomized: {
    ...boolean(false),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'When true, the hover font is customized independently; when false, it mirrors the regular state font',
      },
    },
  },
  signupButtonHoverColor: {
    ...buttonStyleColor(signupButtonStyle, 'textColor'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'button-color-text-primary',
        opacity: 1,
      },
      ai: { description: 'Text color of the submit button on hover' },
    },
  },
  signupButtonHoverFillColor: {
    ...buttonStyleColor(signupButtonStyle, 'fillColor'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'button-color-fill-primary',
        opacity: 1,
      },
      ai: {
        description: 'Background fill color of the submit button on hover',
      },
    },
  },
  signupButtonHoverBorderColor: {
    ...buttonStyleColor(signupButtonStyle, 'borderColor'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-line', opacity: 1 },
      ai: { description: 'Border color of the submit button on hover' },
    },
  },

  // Display toggle parameters (elements panel)
  showTitle,
  showSubtitle,
  showFormSwitchBar,
  showMandatorySSOSubtitle,
} as const;

export type IStylesParams = {
  titleFont: StyleParamType.Font;
  titleColor: StyleParamType.Color;
  subtitleFont: StyleParamType.Font;
  subtitleColor: StyleParamType.Color;
  formSwitchBarPrefixFont: StyleParamType.Font;
  formSwitchBarPrefixColor: StyleParamType.Color;
  formSwitchBarLinkFont: StyleParamType.Font;
  formSwitchBarLinkColor: StyleParamType.Color;
  titleTextAlignment: StyleParamType.Number;
  formSwitchBarTextAlignment: StyleParamType.Number;
  inputFieldBorderStyle: StyleParamType.Number;
  inputFieldBorderColor: StyleParamType.Color;
  inputFieldBorderWidth: StyleParamType.Number;
  inputFieldFillColor: StyleParamType.Color;
  inputFieldCornerRadius: StyleParamType.Number;
  inputFieldTitleFont: StyleParamType.Font;
  inputFieldTitleColor: StyleParamType.Color;
  inputFieldInputFont: StyleParamType.Font;
  inputFieldInputColor: StyleParamType.Color;
  inputFieldDescriptionFont: StyleParamType.Font;
  inputFieldDescriptionColor: StyleParamType.Color;
  inputFieldPlaceholderColor: StyleParamType.Color;
  inputFieldLinkTextColor: StyleParamType.Color;
  inputFieldcheckmarkColor: StyleParamType.Color;
  profilePrivacyFootnoteTextAlignment: StyleParamType.Number;
  profilePrivacyFootnoteColor: StyleParamType.Color;
  profilePrivacyFootnoteFont: StyleParamType.Font;
  // Hover state parameters
  inputFieldHoverFillColor: StyleParamType.Color;
  inputFieldHoverBorderColor: StyleParamType.Color;
  // Error state parameters
  errorColor: StyleParamType.Color;
  errorCornerRadius: StyleParamType.Number;
  // Signup button parameters
  signupButtonState: StyleParamType.Number;
  signupButtonStyle: StyleParamType.Number;
  signupButtonBorderWidth: StyleParamType.Number;
  signupButtonCornerRadius: StyleParamType.Number;
  // Signup button regular state
  signupButtonRegularFont: StyleParamType.Font;
  signupButtonRegularColor: StyleParamType.Color;
  signupButtonRegularFillColor: StyleParamType.Color;
  signupButtonRegularBorderColor: StyleParamType.Color;
  // Signup button hover state
  signupButtonHoverFont: StyleParamType.Font;
  signupButtonHoverFontCustomized: StyleParamType.Boolean;
  signupButtonHoverColor: StyleParamType.Color;
  signupButtonHoverFillColor: StyleParamType.Color;
  signupButtonHoverBorderColor: StyleParamType.Color;
  // FORM BACKGROUND
  formBackground: StyleParamType.Color;
  borderColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  borderRadiusTopLeft: StyleParamType.Number;
  borderRadiusTopRight: StyleParamType.Number;
  borderRadiusBottomLeft: StyleParamType.Number;
  borderRadiusBottomRight: StyleParamType.Number;
  enableShadow: StyleParamType.Boolean;
  shadowAngle: StyleParamType.Number;
  shadowDistance: StyleParamType.Number;
  shadowSize: StyleParamType.Number;
  shadowBlur: StyleParamType.Number;
  shadowColor: StyleParamType.Color;
  boxShadow: StyleParamType.Number;
  // Divider styling parameters
  dividerType: StyleParamType.Number;
  dividerTextFont: StyleParamType.Font;
  dividerTextColor: StyleParamType.Color;
  dividerTextAlignment: StyleParamType.Number;
  dividerLineColor: StyleParamType.Color;
  dividerLineWidth: StyleParamType.Number;
  // Social Signup Options parameters
  socialButtonCornerRadius: StyleParamType.Number;
  socialFontIconSize: StyleParamType.Number;
  socialIconSize: StyleParamType.Number;
  // Google provider parameters
  googleColorTheme: StyleParamType.Number;
  googleBorderColor: StyleParamType.Color;
  googleTextFont: StyleParamType.Font;
  googleTextColor: StyleParamType.Color;
  googleFillColor: StyleParamType.Color;
  googleHoverFillColor: StyleParamType.Color;
  // Facebook provider parameters
  facebookColorTheme: StyleParamType.Number;
  facebookBorderColor: StyleParamType.Color;
  facebookTextFont: StyleParamType.Font;
  facebookTextColor: StyleParamType.Color;
  facebookFillColor: StyleParamType.Color;
  facebookHoverFillColor: StyleParamType.Color;
  // SSO provider parameters
  ssoButtonType: StyleParamType.Number;
  ssoColorTheme: StyleParamType.Number;
  ssoShowIcon: StyleParamType.Boolean;
  // SSO Button mode styling
  ssoButtonTextFont: StyleParamType.Font;
  ssoButtonTextColor: StyleParamType.Color;
  ssoButtonFillColor: StyleParamType.Color;
  ssoButtonBorderColor: StyleParamType.Color;
  // SSO Link mode styling
  ssoLinkTextFont: StyleParamType.Font;
  ssoLinkHoverTextFont: StyleParamType.Font;
  ssoLinkHoverTextFontCustomized: StyleParamType.Boolean;
  ssoLinkTextColor: StyleParamType.Color;
  ssoLinkHoverTextColor: StyleParamType.Color;
  // SSO Button hover state parameters (for Button mode only)
  ssoButtonHoverTextFont: StyleParamType.Font;
  ssoButtonHoverTextFontCustomized: StyleParamType.Boolean;
  ssoButtonHoverTextColor: StyleParamType.Color;
  ssoButtonHoverFillColor: StyleParamType.Color;
  ssoButtonHoverBorderColor: StyleParamType.Color;
  // SSO hover state parameters
  ssoHoverFontBold: StyleParamType.Boolean;
  ssoHoverFontItalic: StyleParamType.Boolean;
  ssoHoverFontUnderline: StyleParamType.Boolean;
  ssoHoverTextColor: StyleParamType.Color;
  ssoHoverFillColor: StyleParamType.Color;
  ssoHoverBorderColor: StyleParamType.Color;
  // Close button parameters
  closeButtonIconColor: StyleParamType.Color;
  closeButtonIconSize: StyleParamType.Number;
  closeButtonBackgroundColor: StyleParamType.Color;
  // Display toggle parameters (elements panel)
  showTitle: StyleParamType.Boolean;
  showSubtitle: StyleParamType.Boolean;
  showFormSwitchBar: StyleParamType.Boolean;
  showMandatorySSOSubtitle: StyleParamType.Boolean;
  // Text parameters
  textFont: StyleParamType.Font;
  textColor: StyleParamType.Color;
};

type ResolvedStylesParams = {
  [P in keyof IStylesParams]: IStyleParam<IStylesParams[P]>;
};

const stylesParams: ResolvedStylesParams = createStylesParams<IStylesParams>(
  STYLE_PARAM_DEFINITIONS,
);

// yoshi-flow-editor extracts param metadata only from the convention-named
// stylesParams file. Spread layoutParams here so the build includes their
// metadata in the generated builder entry for Aria/Harmony.
export default { ...stylesParams, ...resolvedLayoutParams };

export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const numbers = styleParams.numbers;
  const booleans = styleParams.booleans;

  // 4b: Alignment enums
  const titleTextAlign = resolveTextAlign(
    numbers.titleTextAlignment ?? AlignmentOptions.CENTER,
  );
  const submitButtonAlignSelf = resolveFlexAlignment(
    numbers.submitButtonAlignment ?? AlignmentOptions.CENTER,
  );
  const recaptchaAlignItems = resolveFlexAlignment(
    numbers.recaptchaAlignment ?? AlignmentOptions.LEFT,
  );
  const formSwitchBarJustify = resolveJustifyContent(
    numbers.formSwitchBarTextAlignment ?? AlignmentOptions.CENTER,
  );
  const logoJustifyContent = resolveJustifyContent(
    numbers.logoAlignment ?? AlignmentOptions.CENTER,
  );
  const footnoteTextAlign = resolveTextAlign(
    numbers.profilePrivacyFootnoteTextAlignment ?? AlignmentOptions.LEFT,
  );

  // 4c: Shadow offsets
  const angle = numbers.shadowAngle ?? DEFAULTS.shadowAngle;
  const distance = numbers.shadowDistance ?? DEFAULTS.shadowDistance;
  const offsets = getShadowOffsets({ angle, distance });

  // 4d: Close button size
  const closeIconSize =
    numbers.closeButtonIconSize ?? DEFAULTS.closeButtonIconSize;
  const closeButtonSize = Math.round(closeIconSize / 0.75);

  // 4e: Title-subtitle spacing
  const spacingBetweenFormElements =
    numbers.spacingBetweenFormElements ?? DEFAULTS.spacingBetweenFormElements;
  const titleSubtitleSpacing = Math.round(spacingBetweenFormElements * 0.75);

  // 4f: Effective border width
  const borderStyle =
    numbers.inputFieldBorderStyle ?? DEFAULTS.inputFieldBorderStyle;
  const borderWidth =
    numbers.inputFieldBorderWidth ?? DEFAULTS.inputFieldBorderWidth;
  const effectiveBorderWidth =
    borderStyle === BorderStyleOptions.NONE ? 0 : borderWidth;

  // 4g: Social buttons rowGap
  const socialButtonsStyle =
    numbers.socialButtonsStyle ?? SocialButtonStyle.STACK;
  const socialButtonsSpacing =
    numbers.socialButtonsSpacing ?? DEFAULTS.socialButtonsSpacing;
  const socialRowGap =
    socialButtonsStyle === SocialButtonStyle.ICONS
      ? 0
      : socialButtonsStyle === SocialButtonStyle.ROW
      ? spacingBetweenFormElements
      : socialButtonsSpacing;

  // 4h: Bottom padding (Signup only)
  const topPadding =
    numbers.topAndBottomPadding ?? DEFAULTS.topAndBottomPadding;
  const bottomPadding = topPadding;
  const bottomPaddingWithError = Math.round(topPadding * (2 / 3));

  // 4i: Social buttons style layout vars
  const isStackStyle = socialButtonsStyle === SocialButtonStyle.STACK;
  const isRowStyle = socialButtonsStyle === SocialButtonStyle.ROW;
  const isIconStyle = socialButtonsStyle === SocialButtonStyle.ICONS;
  const socialIconSizeVal = numbers.socialIconSize ?? DEFAULTS.socialIconSize;
  const socialFontIconSizeVal =
    numbers.socialFontIconSize ?? DEFAULTS.socialFontIconSize;

  // 4j: Social buttons first (CSS order)
  const socialButtonsFirst =
    numbers.socialButtonsFirst ?? SocialButtonsPlacement.LAST;
  const isSocialFirst = socialButtonsFirst === SocialButtonsPlacement.FIRST;

  // Divider type flags
  const dividerType = numbers.dividerType ?? DividerTypeOptions.LINE_AND_TEXT;
  const isLineAndText = dividerType === DividerTypeOptions.LINE_AND_TEXT;
  const isLineOnly = dividerType === DividerTypeOptions.LINE_ONLY;
  const isTextOnly = dividerType === DividerTypeOptions.TEXT_ONLY;

  // Per-provider border width (theme-dependent: outline=1px, light/dark=0px)
  const googleTheme = numbers.googleColorTheme ?? ProviderColorTheme.OUTLINE;
  const facebookTheme =
    numbers.facebookColorTheme ?? ProviderColorTheme.OUTLINE;
  const ssoTheme = numbers.ssoColorTheme ?? ProviderColorTheme.OUTLINE;

  // SSO display mode
  const isSsoLink =
    (numbers.ssoButtonType ?? SSOButtonType.BUTTON) === SSOButtonType.LINK;

  // 4k: Full submit button width
  const fullSubmitButtonWidth = booleans.fullSubmitButtonWidth ?? true;

  // 4l: Logo size
  const logoSize = numbers.logoSize ?? DEFAULTS.logoSize;

  return {
    // 4b: Alignments
    titleTextAlign,
    submitButtonAlignSelf,
    recaptchaAlignItems,
    formSwitchBarJustify,
    dividerTextAlign: resolveTextAlign(
      numbers.dividerTextAlignment ?? AlignmentOptions.CENTER,
    ),
    dividerJustify: resolveJustifyContent(
      numbers.dividerTextAlignment ?? AlignmentOptions.CENTER,
    ),
    dividerLineDisplay: isTextOnly ? 'none' : 'flex',
    dividerSecondLineDisplay: isLineAndText ? 'flex' : 'none',
    dividerTextDisplay: isLineOnly ? 'none' : 'block',
    dividerFlexDirection: isLineAndText ? 'row' : 'column',
    dividerAlignItems: isLineAndText ? 'center' : 'stretch',
    logoJustifyContent,
    footnoteTextAlign,

    // 4c: Shadow offsets
    shadowOffsetX: `${offsets.xOffset}px`,
    shadowOffsetY: `${offsets.yOffset}px`,

    // 4d: Close button
    closeButtonSize: `${closeButtonSize}px`,
    closeButtonIconSize: `${closeIconSize}px`,

    // 4e: Title-subtitle spacing
    titleSubtitleSpacing: `${titleSubtitleSpacing}px`,

    // 4f: Effective border width
    effectiveBorderWidth,

    // 4f-b: Per-side input border widths (FormStyles maps to --wix-forms-formInputBorder*Width)
    inputBorderLeftWidth:
      borderStyle === BorderStyleOptions.BOTTOM ? 0 : effectiveBorderWidth,
    inputBorderRightWidth:
      borderStyle === BorderStyleOptions.BOTTOM ? 0 : effectiveBorderWidth,
    inputBorderTopWidth:
      borderStyle === BorderStyleOptions.BOTTOM ? 0 : effectiveBorderWidth,
    inputBorderBottomWidth: effectiveBorderWidth,

    // Form field row spacing (between email and password inputs)
    rowSpacing: 12,

    // 4g: Social row gap
    socialRowGap: `${socialRowGap}px`,

    // 4h: Bottom padding (Signup)
    bottomPaddingValue: `${bottomPadding}`,
    bottomPaddingWithError: `${bottomPaddingWithError}`,
    topPaddingValue: `${topPadding}`,

    // 4i: Social buttons style
    socialFlexDirection: isStackStyle ? 'column' : 'row',
    socialFlexWrap: isRowStyle ? 'wrap' : 'nowrap',
    socialButtonWrapperDisplay: isIconStyle
      ? 'block'
      : isRowStyle
      ? 'flex'
      : 'contents',
    socialButtonWrapperMinWidth: isRowStyle ? '115px' : '0',
    socialButtonWrapperFlex: isRowStyle ? '1 1 115px' : 'none',
    socialButtonWrapperWidth: isIconStyle ? 'fit-content' : 'auto',
    stackTextDisplay: isIconStyle ? 'none' : isStackStyle ? 'inline' : 'none',
    shortTextDisplay: isIconStyle ? 'none' : isStackStyle ? 'none' : 'inline',
    socialColumnGap: `${socialButtonsSpacing}px`,

    // 4i-b: Per-button icon/non-icon mode overrides (breakpoint-correct via CSS vars)
    socialBtnFontSize: `${socialFontIconSizeVal}px`,
    socialBtnColumnGap: isIconStyle ? '0px' : '10px',
    socialBtnMinWidth: isIconStyle ? '0px' : '115px',
    socialBtnPadInline: isIconStyle ? '0px' : '15px',
    socialBtnPadBlock: isIconStyle ? '0px' : '7px',
    socialBtnWidth: isIconStyle ? `${socialIconSizeVal}px` : '100%',
    socialBtnHeight: isIconStyle ? `${socialIconSizeVal}px` : 'auto',
    socialBtnSvgSize: `${
      isIconStyle ? Math.round(socialIconSizeVal * 0.5) : socialFontIconSizeVal
    }px`,
    socialBtnSvgTransform: isIconStyle ? 'none' : 'translateY(-1px)',
    socialBtnAffixSize: isIconStyle
      ? `${Math.round(socialIconSizeVal * 0.75)}px`
      : 'auto',
    socialBtnContentDisplay: isIconStyle ? 'none' : 'block',

    // Per-provider border width
    googleBtnBorderWidth:
      googleTheme === ProviderColorTheme.OUTLINE ? '1px' : '0px',
    facebookBtnBorderWidth:
      facebookTheme === ProviderColorTheme.OUTLINE ? '1px' : '0px',
    ssoBtnBorderWidth: ssoTheme === ProviderColorTheme.OUTLINE ? '1px' : '0px',

    // Provider color defaults (Studio viewer doesn't emit CSS vars for params
    // with name:null; these computed fallbacks ensure correct colors always)
    googleFillColorDefault: getProviderColorDefault(
      googleTheme,
      IDP_APPDEFIDS.google,
      'backgroundColor',
    ),
    googleHoverFillColorDefault: getProviderColorDefault(
      googleTheme,
      IDP_APPDEFIDS.google,
      'hoverBackgroundColor',
    ),
    googleTextColorDefault: getProviderColorDefault(
      googleTheme,
      IDP_APPDEFIDS.google,
      'textColor',
    ),
    googleBorderColorDefault: getProviderColorDefault(
      googleTheme,
      IDP_APPDEFIDS.google,
      'borderColor',
    ),
    facebookFillColorDefault: getProviderColorDefault(
      facebookTheme,
      IDP_APPDEFIDS.facebook,
      'backgroundColor',
    ),
    facebookHoverFillColorDefault: getProviderColorDefault(
      facebookTheme,
      IDP_APPDEFIDS.facebook,
      'hoverBackgroundColor',
    ),
    facebookTextColorDefault: getProviderColorDefault(
      facebookTheme,
      IDP_APPDEFIDS.facebook,
      'textColor',
    ),
    facebookBorderColorDefault: getProviderColorDefault(
      facebookTheme,
      IDP_APPDEFIDS.facebook,
      'borderColor',
    ),
    ssoFillColorDefault: getProviderColorDefault(
      ssoTheme,
      IDP_APPDEFIDS.sso,
      'backgroundColor',
    ),
    ssoHoverFillColorDefault: getProviderColorDefault(
      ssoTheme,
      IDP_APPDEFIDS.sso,
      'hoverBackgroundColor',
    ),
    ssoTextColorDefault: getProviderColorDefault(
      ssoTheme,
      IDP_APPDEFIDS.sso,
      'textColor',
    ),
    ssoBorderColorDefault: getProviderColorDefault(
      ssoTheme,
      IDP_APPDEFIDS.sso,
      'borderColor',
    ),
    ssoHoverTextColorDefault: getProviderColorDefault(
      ssoTheme,
      IDP_APPDEFIDS.sso,
      'hoverTextColor',
    ),
    ssoHoverBorderColorDefault: getProviderColorDefault(
      ssoTheme,
      IDP_APPDEFIDS.sso,
      'hoverBorderColor',
    ),

    // Facebook dual-icon toggle (brand/light theme uses white icon, others use blue)
    fbIconDefaultDisplay:
      facebookTheme === ProviderColorTheme.OUTLINE ? 'contents' : 'none',
    fbIconBrandDisplay:
      facebookTheme === ProviderColorTheme.OUTLINE ? 'none' : 'contents',

    // SSO link/button toggle (both rendered, CSS controls which is visible)
    ssoLinkDisplay: isSsoLink ? 'contents' : 'none',
    ssoButtonDisplay: isSsoLink ? 'none' : 'contents',

    // SSO icon visibility (boolean → CSS display toggle)
    ssoIconDisplay: booleans.ssoShowIcon === false ? 'none' : 'flex',

    // 4j: Social buttons first (CSS order)
    // Social first: social(1), divider(2), form(3), submit(4), footnote(5), consent(6)
    // Default:      form(1), submit(2), divider(3), social(4), footnote(5), consent(6)
    orderForm: isSocialFirst ? 3 : 1,
    orderDivider: isSocialFirst ? 2 : 3,
    orderSocial: isSocialFirst ? 1 : 4,
    orderSubmit: isSocialFirst ? 4 : 2,
    orderFootnote: 5,
    orderConsent: 6,

    // 4k: Submit button width
    submitButtonWidth: fullSubmitButtonWidth ? '100%' : 'auto',

    // 4l: Logo size
    logoSizePx: `${logoSize}px`,
  };
};
